/**
 * 装配体基准引用依赖定义。
 *
 * 文件职责：
 * 1. 描述基准点、轴、面与创建它们的参考拓扑之间的稳定依赖；
 * 2. 为 Mate 求解器提供“基准本身固定”与“基准由组件驱动”的纯数据边界；
 * 3. 禁止携带 Three.js 对象或 Vue 响应式状态，确保文档持久化与重载可恢复。
 */

/** 装配体基准依赖协议版本。 */
export const ASSEMBLY_REFERENCE_DEPENDENCY_SCHEMA_VERSION = 1;

/**
 * 装配体基准依赖类型。
 */
export enum AssemblyReferenceDependencyKind {
  /** 默认基准或无参考拓扑的基准，按固定世界参考处理。 */
  Fixed = 'fixed',
  /** 派生基准由单一装配组件驱动，Mate 求解应移动该组件。 */
  SingleComponent = 'single-component',
  /** 派生基准由多个组件共同驱动，当前标准 Mate 求解器不应静默猜测移动侧。 */
  MultiComponent = 'multi-component',
  /** 基准声明了依赖但当前文档无法恢复其参考对象。 */
  MissingReference = 'missing-reference',
}

/**
 * 装配体基准依赖类型的可序列化取值。
 *
 * 设计说明：
 * cad-core 内部优先使用枚举，选择快照和历史文档在运行时可能来自
 * `.d.ts` 暴露的字符串联合类型。这里显式兼容二者，避免 UI 层或旧数据
 * 因为枚举身份不同而绕过求解器诊断。
 */
export type AssemblyReferenceDependencyKindValue =
  | AssemblyReferenceDependencyKind
  | `${AssemblyReferenceDependencyKind}`;

/**
 * 装配体基准引用依赖快照。
 *
 * 设计说明：
 * 1. SOLIDWORKS 中派生 reference geometry 保留父子关系，参与配合时应通过父引用驱动模型；
 * 2. 单组件派生基准可以等价映射到该组件的 Mate 实体，几何仍使用基准自身的 world-space 描述；
 * 3. 多组件派生基准先作为装配体参考几何参与配合，完整父组件联动由依赖图求解扩展。
 */
export interface AssemblyReferenceDependencySnapshot {
  /** 协议版本。 */
  readonly schemaVersion: typeof ASSEMBLY_REFERENCE_DEPENDENCY_SCHEMA_VERSION;
  /** 依赖类型。 */
  readonly kind: AssemblyReferenceDependencyKindValue;
  /** 基准自身稳定引用。 */
  readonly referenceStableRef: string;
  /** 驱动该基准的组件实例 ID 列表。 */
  readonly driverComponentInstanceIds: readonly string[];
  /** 创建基准时记录的稳定拓扑引用。 */
  readonly driverTopologyRefs: readonly AssemblyTopologySelectionRef[];
  /** 诊断说明，供求解器和属性栏展示。 */
  readonly message?: string;
}

/**
 * 克隆装配体拓扑引用。
 *
 * @param ref 拓扑稳定引用。
 * @returns 独立拓扑稳定引用副本。
 */
function cloneAssemblyTopologySelectionRef(
  ref: AssemblyTopologySelectionRef,
): AssemblyTopologySelectionRef {
  return {
    ...ref,
    adjacentFaceRefs: ref.adjacentFaceRefs ? [...ref.adjacentFaceRefs] : undefined,
  };
}

/**
 * 克隆基准引用依赖快照。
 *
 * @param snapshot 依赖快照。
 * @returns 独立依赖快照副本；缺省时返回 `undefined`。
 */
export function cloneAssemblyReferenceDependencySnapshot(
  snapshot: AssemblyReferenceDependencySnapshot | null | undefined,
): AssemblyReferenceDependencySnapshot | undefined {
  if (!snapshot) {
    return undefined;
  }

  return {
    schemaVersion: ASSEMBLY_REFERENCE_DEPENDENCY_SCHEMA_VERSION,
    kind: snapshot.kind,
    referenceStableRef: snapshot.referenceStableRef,
    driverComponentInstanceIds: [...snapshot.driverComponentInstanceIds],
    driverTopologyRefs: snapshot.driverTopologyRefs.map(cloneAssemblyTopologySelectionRef),
    ...(snapshot.message ? { message: snapshot.message } : {}),
  };
}

/**
 * 判断依赖是否由单个组件驱动。
 *
 * @param snapshot 依赖快照。
 * @returns 若可解析为唯一驱动组件则返回 `true`。
 */
export function isSingleComponentAssemblyReferenceDependency(
  snapshot: AssemblyReferenceDependencySnapshot | null | undefined,
): snapshot is AssemblyReferenceDependencySnapshot & {
  readonly kind: AssemblyReferenceDependencyKind.SingleComponent;
} {
  return snapshot?.kind === AssemblyReferenceDependencyKind.SingleComponent
    && snapshot.driverComponentInstanceIds.length === 1;
}

/**
 * 读取派生基准的唯一驱动组件 ID。
 *
 * @param snapshot 依赖快照。
 * @returns 唯一驱动组件 ID；非单驱动时返回 `null`。
 */
export function resolveSingleAssemblyReferenceDependencyDriverId(
  snapshot: AssemblyReferenceDependencySnapshot | null | undefined,
): string | null {
  if (!isSingleComponentAssemblyReferenceDependency(snapshot)) {
    return null;
  }
  return snapshot.driverComponentInstanceIds[0] ?? null;
}
